/**
 * DocumentDetails - Quote/Invoice details and client info
 * PROMPT 40: Financial Documents Unified View Layer
 */
import { Calendar, FileText, Building2, Hash } from "lucide-react";

export function DocumentDetails({
  documentType = "quote", // "quote" or "invoice"
  issueDate,
  dueDate, // For invoices
  validUntil, // For quotes
  project,
  client,
  invoiceNumber, // Reference to original invoice number (if applicable)
  formatDate,
}) {
  const isInvoice = documentType === "invoice";

  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-border">
      {/* Document Details */}
      <div>
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          {isInvoice ? "Invoice Details" : "Quote Details"}
        </h2>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Issue Date:</span>
            <span className="font-medium" data-testid="document-issue-date">
              {formatDate(issueDate)}
            </span>
          </div>
          
          {isInvoice && dueDate && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Due Date:</span>
              <span className="font-medium" data-testid="document-due-date">
                {formatDate(dueDate)}
              </span>
            </div>
          )}
          
          {!isInvoice && validUntil && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Valid Until:</span>
              <span className="font-medium" data-testid="document-valid-until">
                {formatDate(validUntil)}
              </span>
            </div>
          )}
          
          {project && (
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Project:</span>
              <span className="font-medium" data-testid="document-project">
                {project.name}
              </span>
            </div>
          )}

          {invoiceNumber && (
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Reference:</span>
              <span className="font-medium" data-testid="document-reference">
                {invoiceNumber}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Client Details */}
      <div>
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Bill To
        </h2>
        {client ? (
          <div className="space-y-1" data-testid="document-client-info">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold text-foreground">{client.name}</span>
            </div>
            {client.billing_address && (
              <p className="text-sm text-muted-foreground ml-6 whitespace-pre-line">
                {client.billing_address}
              </p>
            )}
            {client.contact_email && (
              <p className="text-sm text-muted-foreground ml-6">
                {client.contact_email}
              </p>
            )}
            {client.contact_phone && (
              <p className="text-sm text-muted-foreground ml-6">
                {client.contact_phone}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">No client information</p>
        )}
      </div>
    </div>
  );
}

export default DocumentDetails;
